import {
	handleRequestRetry,
	WIZARD_SESSION_RETRY_TIME,
} from 'helpers/handleRequestRetry';
import { DevicesEndpoints } from 'api/axios/axiosAPIGroups';
import { AxiosManager } from 'api/axios/axios';
import { X_WIZARD_SESSION_TOKEN } from 'api/axios/constants';
import { getOrCreateWizardSessionRequestId } from 'helpers/requestIdUtils';
import { useHTTPRequestHandler } from './useHTTPRequestHandler';
import { SessionDTO, StatusResponse } from 'api/api_code';
import { setToSessionStorage } from 'helpers/storageUtils';
import { AxiosError } from 'axios';
import { useLoading } from './useLoading';

export const useSession = () => {
	const { requestHandler, cancelForceSession, forceStartSession } =
		useHTTPRequestHandler();
	const { setLoading } = useLoading();

	const startWizardSession = async (
		device_uuid: string,
		field_uuid?: string,
		onSessionStartedCallback?: CallableFunction
	) => {
		const requestId = getOrCreateWizardSessionRequestId();
		return new Promise((resolve) =>
			requestHandler({
				requestPromise:
					DevicesEndpoints.startWizardSessionDevicesDeviceUuidSessionPost(
						device_uuid,
						requestId,
						field_uuid
					),
				onOkCallback: (session: SessionDTO | StatusResponse) => {
					if ((session as StatusResponse).status === 'in_progress') {
						handleRequestRetry({
							callback: () =>
								startWizardSession(
									device_uuid,
									field_uuid,
									onSessionStartedCallback
								),
							time: WIZARD_SESSION_RETRY_TIME,
						});
						setLoading(true);
						return;
					}
					if ((session as StatusResponse).status === 'failed') {
						sessionStorage.removeItem('wizard_session_request_id');
						throw new Error('wizard session failed');
					}
					setToSessionStorage('session_token', session['token']);
					sessionStorage.removeItem('wizard_session_request_id');
					AxiosManager.getInstance().addCommonHeader(
						X_WIZARD_SESSION_TOKEN,
						session['token']
					);
					resolve(onSessionStartedCallback ? onSessionStartedCallback() : true);
					setLoading(false);
				},
				onKo: {
					onKoCallback: (
						error: AxiosError & {
							response: { data: { code: number; message: string } };
						}
					) => {
						if (error?.code === 'ECONNABORTED') {
							handleRequestRetry({
								callback: () =>
									startWizardSession(
										device_uuid,
										field_uuid,
										onSessionStartedCallback
									),
								time: 0,
							});
							setLoading(true);
							return;
						}
						sessionStorage.removeItem('wizard_session_request_id');
						setLoading(false);
						onSessionStartedCallback && onSessionStartedCallback();
					},
					overrideCallback: (error: AxiosError) =>
						error.code === 'ECONNABORTED',
				},
			})
		);
	};
	function detachWizardSession() {
		sessionStorage.removeItem('session_token');
		delete AxiosManager.getInstance().getAxiosInstance().defaults.headers
			.common[X_WIZARD_SESSION_TOKEN];
	}
	const closeWizardSession = async (
		device_uuid: string,
		onClosedCallback?: CallableFunction,
		onCloseErrorCallback?: CallableFunction
	) => {
		requestHandler({
			requestPromise:
				DevicesEndpoints.stopWizardSessionDevicesDeviceUuidSessionDelete(
					device_uuid
				),
			onOkCallback: () => {
				detachWizardSession();
				onClosedCallback && onClosedCallback();
			},
			onKo: {
				onKoCallback: () => {
					detachWizardSession();
					onCloseErrorCallback && onCloseErrorCallback();
				},
			},
		});
	};
	return {
		startWizardSession,
		closeWizardSession,
		cancelForceSession,
		forceStartSession,
	};
};
